import {
  DocumentProcessingController,
  useDocumentProcessing,
} from '@launcher/context/DocumentProcessing.context';
import { useFeatureFlags } from '@launcher/context/FeatureFlags.context';
import { isDashboardRoute } from '@utils/route.utils';
import { useLocation } from 'react-router-dom';

import { DocumentImportStatus, DocumentProgress } from './DocumentImportStatus.component';

export const SendingDocumentImportStatus = () => {
  const featureFlags = useFeatureFlags();
  const useNewUploadUI = featureFlags['enable-new-import-analyze-flow'];
  const { documents, childToParentIndex } = useDocumentProcessing();
  const { pathname } = useLocation();

  // Only show on dashboard routes, not on detail pages
  if (!useNewUploadUI || documents.size === 0 || !isDashboardRoute(pathname)) {
    return null;
  }

  const documentsArray = Array.from(documents.values());

  // Filter out children (they are displayed within their parent)
  const parentsOnly = documentsArray.filter((doc) => {
    const docId = doc.documentId || doc.file.uid;
    return !childToParentIndex.has(docId);
  });

  const mappedDocuments: DocumentProgress[] = [];

  parentsOnly.forEach((doc) => {
    const hasChildren = doc.children && doc.children.length > 0;

    if (hasChildren) {
      // Separate completed children from in-progress children
      const completedChildren = doc.children!.filter(
        (child) =>
          child.status === 'TERMINATED' ||
          child.status === 'ANALYZE_UNAVAILABLE' ||
          child.status === 'FAILURE',
      );

      const inProgressChildren = doc.children!.filter(
        (child) =>
          child.status !== 'TERMINATED' &&
          child.status !== 'ANALYZE_UNAVAILABLE' &&
          child.status !== 'FAILURE',
      );

      // Add completed children as separate documents with parentName
      completedChildren.forEach((child) => {
        mappedDocuments.push({
          id: child.documentId || child.file.uid,
          key: child.file.uid, // Stable key
          name: child.file.name,
          status: child.status,
          error: child.error,
          parentName: doc.file.name || 'Document parent',
        });
      });

      // Only show parent if there are in-progress children
      if (inProgressChildren.length > 0) {
        mappedDocuments.push({
          id: doc.documentId || doc.file.uid,
          key: doc.file.uid, // Stable key
          name: doc.file.name,
          status: doc.status,
          error: doc.error,
          children: inProgressChildren.map((child) => ({
            id: child.documentId || child.file.uid,
            key: child.file.uid, // Stable key
            name: child.file.name,
            status: child.status,
            error: child.error,
          })),
          totalChildren: doc.children!.length, // Total including completed
        });
      }
    } else {
      // Regular document without children
      mappedDocuments.push({
        id: doc.documentId || doc.file.uid,
        key: doc.file.uid, // Stable key
        name: doc.file.name,
        status: doc.status,
        error: doc.error,
      });
    }
  });

  const handleClose = () => {
    DocumentProcessingController.clearAll();
  };

  return <DocumentImportStatus documents={mappedDocuments} onClose={handleClose} />;
};
